import { AddressElement, Elements } from '@stripe/react-stripe-js';
import type {
  Appearance,
  StripeAddressElementChangeEvent,
  StripeElementLocale,
} from '@stripe/stripe-js';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getAuthUser } from 'store/selectors/auth';
import { getStripePromise } from 'utils/stripeClient';

import {
  type InvoiceBillingDefaults,
  invoiceBillingDefaultsKey,
  MAX_BILLING_NAME_LENGTH,
} from './invoiceBillingDefaults';
import InvoicePayerEmailsField, {
  type InvoicePayerEmailsFieldHandle,
} from './InvoicePayerEmailsField';
import * as Ch from './styled';

export { MAX_BILLING_NAME_LENGTH };
export { MAX_BILLING_EMAILS } from './InvoicePayerEmailsField';

const BILLING_EMAIL_PLACEHOLDER = 'accounting@club.ch';
/** CHF invoice billing is always Switzerland → single VAT type, no type picker. */
const CHF_TAX_ID_TYPE = 'ch_vat' as const;
const CHF_VAT_PLACEHOLDER = 'CHE-123.456.789 MWST';

export type InvoiceBillingTaxId = {
  businessName: string;
  taxId: string;
  taxIdType: typeof CHF_TAX_ID_TYPE;
  externalTaxIdType: typeof CHF_TAX_ID_TYPE;
};

export type InvoiceBillingData = {
  name: string;
  address: StripeAddressElementChangeEvent['value'];
  taxId: InvoiceBillingTaxId | null;
  billingEmails: string[];
};

export type InvoiceChfBillingHandle = {
  getBillingData: () => InvoiceBillingData | null;
  validateBillingFields: () => boolean;
};

type InvoiceChfBillingFieldsProps = {
  active: boolean;
  defaultCountry?: string;
  /** Prefill from Redux / GET subscriptions (name, address, tax, emails). */
  defaults?: InvoiceBillingDefaults;
  onReadyChange?: (ready: boolean) => void;
};

const stripeAppearance: Appearance = {
  theme: 'stripe',
  labels: 'floating',
  variables: {
    borderRadius: '10px',
    fontSizeBase: '14px',
    colorText: '#304659',
    colorTextSecondary: '#4F667A',
    colorTextPlaceholder: '#8298AB',
    colorPrimary: '#33ABA7',
    colorDanger: '#C53030',
  },
  rules: {
    '.Label': {
      transition: 'none',
      fontWeight: '400',
    },
    '.Label--resting': {
      color: '#8298AB',
      fontSize: '14px',
      opacity: '1',
    },
    // Disappear instead of floating to the border (no animated / above label).
    '.Label--floating': {
      opacity: '0',
      fontSize: '0px',
    },
    '.Input': {
      border: '1.5px solid #E5E9E9',
      boxShadow: 'none',
      paddingTop: '12px',
      paddingBottom: '12px',
    },
    '.Input:focus': {
      border: '1.5px solid #33ABA7',
      boxShadow: 'none',
      outline: 'none',
    },
  },
};

type InnerProps = {
  defaultCountry: string;
  defaults: InvoiceBillingDefaults;
  onReadyChange?: (ready: boolean) => void;
};

const InvoiceChfBillingFieldsInner = forwardRef<
  InvoiceChfBillingHandle,
  InnerProps
>(function InvoiceChfBillingFieldsInner(
  { defaultCountry, defaults, onReadyChange },
  ref,
) {
  const { t } = useTranslation();
  const profile = useSelector(getAuthUser);
  const [name, setName] = useState(defaults.name);
  const [addressData, setAddressData] = useState<
    StripeAddressElementChangeEvent['value'] | null
  >(null);
  const [addressComplete, setAddressComplete] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [vatNumber, setVatNumber] = useState(defaults.taxId?.taxId ?? '');
  const [emails, setEmails] = useState<string[]>(defaults.billingEmails);
  const emailFieldRef = useRef<InvoicePayerEmailsFieldHandle>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const addressFieldRef = useRef<HTMLDivElement>(null);
  const nameEditedRef = useRef(false);

  useEffect(() => {
    if (nameEditedRef.current) return;
    setName(defaults.name);
  }, [defaults.name]);

  useEffect(() => {
    setVatNumber(defaults.taxId?.taxId ?? '');
  }, [defaults.taxId?.taxId]);

  const trimmedName = name.trim().slice(0, MAX_BILLING_NAME_LENGTH);
  const trimmedVat = vatNumber.trim();
  const taxIdData: InvoiceBillingTaxId | null = trimmedVat
    ? {
        businessName: '',
        taxId: trimmedVat,
        taxIdType: CHF_TAX_ID_TYPE,
        externalTaxIdType: CHF_TAX_ID_TYPE,
      }
    : null;
  const ready = Boolean(trimmedName) && addressComplete;

  useEffect(() => {
    onReadyChange?.(ready);
  }, [ready, onReadyChange]);

  useImperativeHandle(
    ref,
    () => ({
      validateBillingFields: () => {
        const nameValid = Boolean(trimmedName);
        const addressValid = addressComplete;
        setNameError(!nameValid);
        setAddressError(!addressValid);
        if (!nameValid) {
          nameInputRef.current?.focus();
        } else if (!addressValid) {
          addressFieldRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
        return nameValid && addressValid;
      },
      getBillingData: () => {
        if (!addressData || !trimmedName) return null;
        const flushed = emailFieldRef.current?.flush();
        return {
          name: trimmedName,
          address: addressData,
          taxId: taxIdData,
          billingEmails: flushed?.emails ?? emails,
        };
      },
    }),
    [addressComplete, addressData, trimmedName, taxIdData, emails],
  );

  const addressOptions = useMemo(
    () => ({
      mode: 'billing' as const,
      allowedCountries: [defaultCountry],
      // Requires `address_element_hide_name_field_beta_1` on loadStripe.
      // Debtor name is our "Organization or name" field above.
      fields: {
        name: 'never' as const,
      },
      defaultValues: {
        address: {
          country: defaults.address?.country || defaultCountry,
          ...(defaults.address?.line1 ? { line1: defaults.address.line1 } : {}),
          ...(defaults.address?.line2 ? { line2: defaults.address.line2 } : {}),
          ...(defaults.address?.city ? { city: defaults.address.city } : {}),
          ...(defaults.address?.state ? { state: defaults.address.state } : {}),
          ...(defaults.address?.postal_code
            ? { postal_code: defaults.address.postal_code }
            : {}),
        },
      },
    }),
    [defaultCountry, defaults.address],
  );

  return (
    <>
      <Ch.InvoiceFieldGroup>
        <Ch.InvoiceFieldLabel htmlFor="chf-billing-name">
          {t('subscriptions.checkout.chf.name')}
          <Ch.InvoiceRequiredTag aria-hidden="true">*</Ch.InvoiceRequiredTag>
        </Ch.InvoiceFieldLabel>
        <Ch.InvoiceTextInput
          ref={nameInputRef}
          id="chf-billing-name"
          name="billingName"
          type="text"
          autoComplete="organization"
          maxLength={MAX_BILLING_NAME_LENGTH}
          aria-label={t('subscriptions.checkout.chf.name')}
          aria-required="true"
          aria-invalid={nameError}
          value={name}
          onChange={(e) => {
            nameEditedRef.current = true;
            setName(e.target.value);
            if (e.target.value.trim()) setNameError(false);
          }}
        />
        {nameError ? (
          <Ch.InvoiceFieldError role="alert">
            {t('subscriptions.checkout.chf.nameRequired')}
          </Ch.InvoiceFieldError>
        ) : null}
      </Ch.InvoiceFieldGroup>

      <Ch.InvoiceFieldGroup ref={addressFieldRef}>
        <Ch.InvoiceFieldLabel as="span">
          {t('subscriptions.checkout.chf.address')}
          <Ch.InvoiceRequiredTag aria-hidden="true">*</Ch.InvoiceRequiredTag>
        </Ch.InvoiceFieldLabel>
        <Ch.StripeElementField aria-invalid={addressError} aria-required="true">
          <AddressElement
            options={addressOptions}
            onChange={(event) => {
              setAddressComplete(event.complete);
              setAddressData(event.complete ? event.value : null);
              if (event.complete) setAddressError(false);
            }}
          />
        </Ch.StripeElementField>
        {addressError ? (
          <Ch.InvoiceFieldError role="alert">
            {t('subscriptions.checkout.chf.addressRequired')}
          </Ch.InvoiceFieldError>
        ) : null}
      </Ch.InvoiceFieldGroup>

      <Ch.InvoiceFieldGroup>
        <Ch.InvoiceFieldLabel htmlFor="chf-billing-vat">
          {t('subscriptions.checkout.chf.vatNumber')}
          <Ch.InvoiceOptionalTag>
            {t('subscriptions.checkout.chf.optional')}
          </Ch.InvoiceOptionalTag>
        </Ch.InvoiceFieldLabel>
        <Ch.InvoiceTextInput
          id="chf-billing-vat"
          name="billingVat"
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder={CHF_VAT_PLACEHOLDER}
          aria-label={t('subscriptions.checkout.chf.vatNumber')}
          value={vatNumber}
          onChange={(e) => setVatNumber(e.target.value)}
        />
      </Ch.InvoiceFieldGroup>

      <InvoicePayerEmailsField
        ref={emailFieldRef}
        emails={emails}
        onChange={setEmails}
        inputId="chf-billing-email"
        label={t('subscriptions.checkout.chf.billingEmail')}
        hint={t('subscriptions.checkout.chf.recipientsHint', {
          email: profile?.email ?? '',
        })}
        placeholder={BILLING_EMAIL_PLACEHOLDER}
        optional
      />
    </>
  );
});

const stripePromise = getStripePromise();

const EMPTY_DEFAULTS: InvoiceBillingDefaults = {
  name: '',
  address: null,
  taxId: null,
  billingEmails: [],
};

const InvoiceChfBillingFields = forwardRef<
  InvoiceChfBillingHandle,
  InvoiceChfBillingFieldsProps
>(function InvoiceChfBillingFields(
  { active, defaultCountry = 'CH', defaults = EMPTY_DEFAULTS, onReadyChange },
  ref,
) {
  const { t, i18n } = useTranslation();
  const remountKey = invoiceBillingDefaultsKey(defaults);

  if (!active) return null;

  if (!stripePromise) {
    return (
      <Ch.InvoiceFormSection>
        <Ch.InvoiceFormError role="alert">
          {t('subscriptions.checkout.invoiceBillingStripeMissing')}
        </Ch.InvoiceFormError>
      </Ch.InvoiceFormSection>
    );
  }

  return (
    <Ch.InvoiceFormSection aria-labelledby="invoice-billing-heading">
      <Ch.InvoiceFormSectionTitle id="invoice-billing-heading">
        {t('subscriptions.checkout.chf.title')}
      </Ch.InvoiceFormSectionTitle>
      <Ch.InvoiceSectionSubtitle>
        {t('subscriptions.checkout.chf.subtitle')}
      </Ch.InvoiceSectionSubtitle>
      <Elements
        key={remountKey}
        stripe={stripePromise}
        options={{
          appearance: stripeAppearance,
          locale: (i18n.language?.split('-')[0] || 'en') as StripeElementLocale,
        }}
      >
        <InvoiceChfBillingFieldsInner
          ref={ref}
          defaultCountry={defaultCountry}
          defaults={defaults}
          onReadyChange={onReadyChange}
        />
      </Elements>
    </Ch.InvoiceFormSection>
  );
});

export default InvoiceChfBillingFields;
